import type { Personnel, CastAssignment } from '../personnel/types';
import type { ScheduleEvent, RehearsalScene } from '../scheduler/types';
import type { Act, Character } from '../show-structure/types';
import { getPrimaryActorId } from '../personnel/utils/cast-assignment-selectors';
import { expandScheduleEventAttendeeIds } from '../scheduler/attendees';
import { shouldUseSavedScheduleAttendees } from '../scheduler/call-resolution';

export interface RequiredActorContext {
  actors: Personnel[];
  personnel?: Personnel[];
  characters: Character[];
  acts: Act[];
  rehearsalScenes: RehearsalScene[];
  castAssignments: CastAssignment[];
}

export function getRequiredActorIdsForEvent(
  event: ScheduleEvent,
  context: RequiredActorContext
): Set<string> {
  if (shouldUseSavedScheduleAttendees(event)) {
    return savedAttendeeActorIds(event, context);
  }

  if (event.type === 'scene') return sceneRequiredActorIds(event, context);
  if (event.type === 'act') return actRequiredActorIds(event, context);
  if (event.type === 'rehearsal-scene') return rehearsalSceneRequiredActorIds(event, context);
  if (isFullCastTemplateEvent(event)) return activeActorIds(context.actors);

  return new Set();
}

export function getAttendanceRequiredActorIdsForEvent(
  event: ScheduleEvent,
  context: RequiredActorContext
): Set<string> {
  const actorIds = getRequiredActorIdsForEvent(event, context);

  if (shouldUseSavedScheduleAttendees(event)) {
    return actorIds;
  }

  if (
    actorIds.size === 0
    && !event.attendees?.length
    && (event.type === 'tech' || event.type === 'performance')
  ) {
    return new Set(
      context.actors
        .filter(actor => !actor.deleted)
        .map(actor => actor.id)
    );
  }

  return actorIds;
}

export function getConflictedRequiredActorIds(
  event: ScheduleEvent,
  context: RequiredActorContext
): string[] {
  const requiredActorIds = getAttendanceRequiredActorIdsForEvent(event, context);
  return (context.personnel ?? context.actors)
    .filter(person => requiredActorIds.has(person.id))
    .filter(person => person.conflicts?.includes(event.date))
    .map(person => person.id);
}

function activeActorIds(actors: Personnel[]): Set<string> {
  return new Set(actors.filter(actor => !actor.deleted).map(actor => actor.id));
}

function primaryActorIdsForCharacters(
  characterIds: Iterable<string>,
  castAssignments: CastAssignment[]
): Set<string> {
  const actorIds = new Set<string>();

  for (const characterId of characterIds) {
    const actorId = getPrimaryActorId(characterId, castAssignments);
    if (actorId) actorIds.add(actorId);
  }

  return actorIds;
}

function savedAttendeeActorIds(
  event: ScheduleEvent,
  context: RequiredActorContext
): Set<string> {
  const savedAttendeePersonnel = context.personnel ?? context.actors;
  const activeSavedAttendeeIds = activeActorIds(savedAttendeePersonnel);

  return new Set(
    expandScheduleEventAttendeeIds(event.attendees ?? [], savedAttendeePersonnel, {
      characters: context.characters,
      castAssignments: context.castAssignments,
    }).filter(personnelId => activeSavedAttendeeIds.has(personnelId))
  );
}

function sceneRequiredActorIds(
  event: ScheduleEvent,
  context: RequiredActorContext
): Set<string> {
  if (!event.targetId) return new Set();
  const targetId = event.targetId;
  const characterIds = context.characters
    .filter(character => character.sceneIds.includes(targetId) && !character.deleted)
    .map(character => character.id);
  return primaryActorIdsForCharacters(characterIds, context.castAssignments);
}

function actRequiredActorIds(
  event: ScheduleEvent,
  context: RequiredActorContext
): Set<string> {
  if (!event.targetId) return new Set();
  const act = context.acts.find(candidate => candidate.id === event.targetId);
  if (!act) return new Set();

  const sceneIds = new Set(act.sceneIds);
  const characterIds = context.characters
    .filter(character => character.sceneIds.some(sceneId => sceneIds.has(sceneId)) && !character.deleted)
    .map(character => character.id);
  return primaryActorIdsForCharacters(characterIds, context.castAssignments);
}

function rehearsalSceneRequiredActorIds(
  event: ScheduleEvent,
  context: RequiredActorContext
): Set<string> {
  if (!event.targetId) return new Set();
  const rehearsalScene = context.rehearsalScenes.find(candidate => candidate.id === event.targetId);
  return primaryActorIdsForCharacters(rehearsalScene?.characterIds ?? [], context.castAssignments);
}

function isFullCastTemplateEvent(event: ScheduleEvent): boolean {
  return event.type === 'run' || event.type === 'tech' || event.type === 'performance';
}
